import type {
  FullStatsDto,
  PreflopOrMatrix,
  PreflopOrStatId,
  ThreeBetVsMatrix,
} from '@poker-stats/contracts';

export type PostflopCell = {
  value: number | null;
  sample?: number | null;
};

export type PostflopTableData = {
  heads: string[];
  rows: Array<{ label: string; title?: string; cells: PostflopCell[] }>;
  streetColumnLabel: string;
};

export type PostflopSubTab = {
  id: string;
  label: string;
};

export type PostflopPanelContent = {
  subTabs?: PostflopSubTab[];
  tables: Array<{ title?: string; table: PostflopTableData | null }>;
};

type ColumnDef = {
  name: string;
  values: Array<number | null>;
  samples?: Array<number | null>;
};

const STREETS = ['FLP', 'TRN', 'RVR'] as const;

const PREFLOP_POSITIONS = ['tot', 'ep', 'mp', 'co', 'btn', 'sb', 'bb'] as const;

const THREEBET_VS_HERO_KEYS = ['ep', 'mp', 'co', 'btn', 'sb', 'bb'] as const;

const THREEBET_VS_VILLAIN_KEYS = ['tot', 'ep', 'mp', 'co', 'btn', 'sb'] as const;

const PREFLOP_OR_DECIMAL_STATS = new Set<PreflopOrStatId>([
  'fourbr',
  'fourbrip',
  'fourbroop',
  'c3br',
  'c3brip',
  'c3broop',
]);

const PREFLOP_OR_TOT_ROWS: Array<{ key: PreflopOrStatId; label: string }> = [
  { key: 'openraise', label: 'R1' },
  { key: 'foldtothreebet', label: 'F3b' },
  { key: 'callthreebet', label: 'C3b' },
  { key: 'c3br', label: 'C3br' },
  { key: 'fourbet', label: '4bet' },
  { key: 'fourbr', label: '4br' },
  { key: 'foldtofivebet', label: 'F5b' },
  { key: 'isoraisev1', label: 'ISO1' },
  { key: 'isoraisev2', label: 'ISO2' },
  { key: 'isoraisev3', label: 'ISO3' },
  { key: 'limp', label: 'Limp' },
  { key: 'limpfold', label: 'L-F' },
  { key: 'limpraise', label: 'L-R' },
];

const PREFLOP_OR_IP_ROWS: Array<{ key: PreflopOrStatId; label: string }> = [
  { key: 'openraise', label: 'R1' },
  { key: 'foldtothreebetip', label: 'F3b' },
  { key: 'callthreebetip', label: 'C3b' },
  { key: 'c3brip', label: 'C3br' },
  { key: 'fourbetip', label: '4bet' },
  { key: 'fourbrip', label: '4br' },
  { key: 'foldtofivebetip', label: 'F5b' },
  { key: 'isoraisev1ip', label: 'ISO1' },
];

const PREFLOP_OR_OOP_ROWS: Array<{ key: PreflopOrStatId; label: string }> = [
  { key: 'openraise', label: 'R1' },
  { key: 'foldtothreebetoop', label: 'F3b' },
  { key: 'callthreebetoop', label: 'C3b' },
  { key: 'c3broop', label: 'C3br' },
  { key: 'fourbetoop', label: '4bet' },
  { key: 'fourbroop', label: '4br' },
  { key: 'foldtofivebetoop', label: 'F5b' },
  { key: 'isoraisev1oop', label: 'ISO1' },
];

function col(
  name: string,
  values: Array<number | null | undefined>,
  samples?: Array<number | null | undefined>,
): ColumnDef {
  return {
    name,
    values: values.map((value) => (value == null ? null : value)),
    samples: samples?.map((value) => (value == null ? null : value)),
  };
}

function columnsToTable(columns: ColumnDef[], streetColumnLabel = ''): PostflopTableData {
  return {
    streetColumnLabel,
    heads: columns.map((item) => item.name),
    rows: STREETS.map((street, streetIndex) => ({
      label: street,
      cells: columns.map((column) => ({
        value: column.values[streetIndex] ?? null,
        sample: column.samples?.[streetIndex] ?? null,
      })),
    })),
  };
}

function pickColumns(columns: ColumnDef[], names: string[]): ColumnDef[] {
  return names
    .map((name) => columns.find((column) => column.name === name))
    .filter((column): column is ColumnDef => column != null);
}

function formatPreflopValue(
  value: number | null | undefined,
  statId: PreflopOrStatId,
): number | null {
  if (value == null) {
    return null;
  }

  if (PREFLOP_OR_DECIMAL_STATS.has(statId)) {
    return +value.toFixed(1);
  }

  return Math.round(value);
}

function formatThreeBetValue(value: number | null | undefined): number | null {
  if (value == null) {
    return null;
  }

  return Number.isInteger(value) ? value : +value.toFixed(1);
}

function positionLabel(key: (typeof PREFLOP_POSITIONS)[number]) {
  if (key === 'tot') return 'TOT';
  if (key === 'btn') return 'BTN';
  return key.toUpperCase();
}

function villainLabel(key: (typeof THREEBET_VS_VILLAIN_KEYS)[number]) {
  if (key === 'tot') return 'TOT';
  if (key === 'btn') return 'BTN';
  return key.toUpperCase();
}

function heroPositionLabel(key: (typeof THREEBET_VS_HERO_KEYS)[number]) {
  if (key === 'btn') return 'BTN';
  if (key === 'sb') return 'SB';
  if (key === 'bb') return 'BB';
  return key.toUpperCase();
}

function heroLabel(key: (typeof THREEBET_VS_HERO_KEYS)[number], cc = false) {
  const prefix = heroPositionLabel(key);
  return cc ? `${prefix} CC` : `${prefix} 3b`;
}

function buildPreflopOrTable(
  matrix: PreflopOrMatrix | undefined,
  statRows: Array<{ key: PreflopOrStatId; label: string }>,
): PostflopTableData | null {
  if (!matrix) {
    return null;
  }

  return {
    streetColumnLabel: '',
    heads: PREFLOP_POSITIONS.map(positionLabel),
    rows: statRows.map((row) => ({
      label: row.label,
      cells: PREFLOP_POSITIONS.map((pos) => {
        const cell = matrix[pos]?.[row.key];
        return {
          value: formatPreflopValue(cell?.value, row.key),
          sample: cell?.sample ?? null,
        };
      }),
    })),
  };
}

function buildThreeBetVsTable(
  matrix: ThreeBetVsMatrix | undefined,
  cc = false,
): PostflopTableData | null {
  if (!matrix) {
    return null;
  }

  return {
    streetColumnLabel: '',
    heads: THREEBET_VS_VILLAIN_KEYS.map(villainLabel),
    rows: THREEBET_VS_HERO_KEYS.map((heroKey) => ({
      label: heroPositionLabel(heroKey),
      title: heroLabel(heroKey, cc),
      cells: THREEBET_VS_VILLAIN_KEYS.map((villainKey) => {
        const cell = matrix[heroKey]?.[villainKey];
        return {
          value: formatThreeBetValue(cell?.value),
          sample: cell?.sample ?? null,
        };
      }),
    })),
  };
}

function buildGeneralColumns(stats: Partial<FullStatsDto>): ColumnDef[] {
  return [
    col(
      'CBet',
      [stats.cbetflop, stats.cbetturn, stats.cbetriver],
      [stats.cbetfloppossible, stats.cbetturnpossible, stats.cbetriverpossible],
    ),
    col(
      'FvCb',
      [stats.foldflopcbet, stats.foldturncbet, stats.foldrivercbet],
      [stats.facingflopcbet, stats.facingturncbet, stats.facingrivercbet],
    ),
    col(
      'RCb',
      [
        stats.raisedflopcontinuationbet,
        stats.raisedturncontinuationbet,
        stats.raisedrivercontinuationbet,
      ],
      [stats.facingflopcbet, stats.facingturncbet, stats.facingrivercbet],
    ),
    col(
      'Bet',
      [stats.flopbet, stats.turnbet, stats.riverbet],
      [stats.flopbetpossible, stats.turnbetpossible, stats.riverbetpossible],
    ),
    col(
      'B-F',
      [stats.betfoldflop, stats.betfoldturn, stats.betfoldriver],
      [stats.betfoldfloppossible, stats.betfoldturnpossible, stats.betfoldriverpossible],
    ),
    col(
      'FvBet',
      [stats.flopfold, stats.turnfold, stats.riverfold],
      [stats.flopfoldpossible, stats.turnfoldpossible, stats.riverfoldpossible],
    ),
    col(
      'RBet',
      [stats.flopraise, stats.turnraise, stats.riverraise],
      [stats.flopraisepossible, stats.turnraisepossible, stats.riverraisepossible],
    ),
    col(
      'W after R',
      [stats.flopwafterr, stats.turnwafterr, stats.riverwafterr],
      [stats.flopwafterrpossible, stats.turnwafterrpossible, stats.riverwafterrpossible],
    ),
    col(
      'Donk',
      [stats.donkflop, stats.donkturn, stats.donkriver],
      [stats.donkfloppossible, stats.donkturnpossible, stats.donkriverpossible],
    ),
    col(
      'xR',
      [stats.xrflop, stats.xrturn, stats.xrriver],
      [stats.xrfloppossible, stats.xrturnpossible, stats.xrriverpossible],
    ),
    col(
      'Agf %',
      [stats.flopagrfactor, stats.turnagrfactor, stats.riveragrfactor],
      [stats.flopagfpossible, stats.turnagfpossible, stats.riveragfpossible],
    ),
  ];
}

function buildDonkColumns(stats: Partial<FullStatsDto>): ColumnDef[] {
  return [
    col(
      'Donk',
      [stats.donkflop, stats.donkturn, stats.donkriver],
      [stats.donkfloppossible, stats.donkturnpossible, stats.donkriverpossible],
    ),
    col(
      'D 3bp',
      [stats.donk3betpflop, stats.donk3betpturn, stats.donk3betpriver],
      [stats.donk3betpfloppossible, stats.donk3betpturnpossible, stats.donk3betpriverpossible],
    ),
    col(
      'FvD',
      [stats.foldvsdonkflop, stats.foldvsdonkturn, stats.foldvsdonkriver],
      [stats.facingflopdonk, stats.facingturndonk, stats.facingriverdonk],
    ),
    col(
      'D-fold',
      [stats.donkfoldflop, stats.donkfoldturn, stats.donkfoldriver],
      [stats.donkfoldfloppossible, stats.donkfoldturnpossible, stats.donkfoldriverpossible],
    ),
  ];
}

function buildCbetColumns(stats: Partial<FullStatsDto>): ColumnDef[] {
  return [
    col(
      'CBet',
      [stats.cbetflop, stats.cbetturn, stats.cbetriver],
      [stats.cbetfloppossible, stats.cbetturnpossible, stats.cbetriverpossible],
    ),
    col('Cb-fold', [stats.cbfoldraise, null, null], [stats.cbfoldraisepossible, null, null]),
    col(
      'RCb',
      [
        stats.raisedflopcontinuationbet,
        stats.raisedturncontinuationbet,
        stats.raisedrivercontinuationbet,
      ],
      [stats.facingflopcbet, stats.facingturncbet, stats.facingrivercbet],
    ),
    col(
      'CBet IP',
      [stats.cbetflopip, stats.cbetturnip, stats.cbetriverip],
      [stats.cbetflopippossible, stats.cbetturnippossible, stats.cbetriverippossible],
    ),
    col('Cb-f IP', [stats.cbfoldraiseip, null, null], [stats.cbfoldraiseippossible, null, null]),
    col(
      'CBet OOP',
      [stats.cbetflopoop, stats.cbetturnoop, stats.cbetriveroop],
      [stats.cbetflopooppossible, stats.cbetturnooppossible, stats.cbetriverooppossible],
    ),
    col('Cb-f OOP', [stats.cbfoldraiseoop, null, null], [stats.cbfoldraiseooppossible, null, null]),
    col(
      'x_F',
      [stats.x_fflop, stats.x_ffturn, stats.x_ffriver],
      [stats.x_ffloppossible, stats.x_ffturnpossible, stats.x_ffriverpossible],
    ),
    col('bxb IP', [null, null, stats.bxbip], [null, null, stats.bxbippossible]),
    col('bxf IP', [null, null, stats.bxfip], [null, null, stats.bxfippossible]),
    col('bxb OOP', [null, null, stats.bxboop], [null, null, stats.bxbooppossible]),
    col('bxf OOP', [null, null, stats.bxfoop], [null, null, stats.bxfooppossible]),
    col('Cb-c&f t', [null, stats.cbcallfoldturn, null], [null, stats.cbcallfoldturnpossible, null]),
    col(
      'Cb-c&c t&f r',
      [null, null, stats.cbcallcallfoldriver],
      [null, null, stats.cbcallcallfoldriverpossible],
    ),
  ];
}

function buildVsCbColumns(stats: Partial<FullStatsDto>): ColumnDef[] {
  return [
    col(
      'FvCb',
      [stats.foldflopcbet, stats.foldturncbet, stats.foldrivercbet],
      [stats.facingflopcbet, stats.facingturncbet, stats.facingrivercbet],
    ),
    col(
      'RCb',
      [
        stats.raisedflopcontinuationbet,
        stats.raisedturncontinuationbet,
        stats.raisedrivercontinuationbet,
      ],
      [stats.facingflopcbet, stats.facingturncbet, stats.facingrivercbet],
    ),
    col(
      'FvCB IP',
      [stats.foldflopcbetip, stats.foldturncbetip, stats.foldrivercbetip],
      [stats.foldflopcbetippossible, stats.foldturncbetippossible, stats.foldrivercbetippossible],
    ),
    col(
      'RCb IP',
      [
        stats.raisedflopcontinuationbetip,
        stats.raisedturncontinuationbetip,
        stats.raisedrivercontinuationbetip,
      ],
      [
        stats.raisedflopcontinuationbetippossible,
        stats.raisedturncontinuationbetippossible,
        stats.raisedrivercontinuationbetippossible,
      ],
    ),
    col(
      'FvCB OOP',
      [stats.foldflopcbetoop, stats.foldturncbetoop, stats.foldrivercbetoop],
      [
        stats.foldflopcbetooppossible,
        stats.foldturncbetooppossible,
        stats.foldrivercbetooppossible,
      ],
    ),
    col(
      'RCb OOP',
      [
        stats.raisedflopcontinuationbetoop,
        stats.raisedturncontinuationbetoop,
        stats.raisedrivercontinuationbetoop,
      ],
      [
        stats.raisedflopcontinuationbetooppossible,
        stats.raisedturncontinuationbetooppossible,
        stats.raisedrivercontinuationbetooppossible,
      ],
    ),
  ];
}

function buildBxColumns(stats: Partial<FullStatsDto>): ColumnDef[] {
  return [
    col(
      'Bet',
      [stats.flopbet, stats.turnbet, stats.riverbet],
      [stats.flopbetpossible, stats.turnbetpossible, stats.riverbetpossible],
    ),
    col(
      'FvBet',
      [stats.flopfold, stats.turnfold, stats.riverfold],
      [stats.flopfoldpossible, stats.turnfoldpossible, stats.riverfoldpossible],
    ),
    col(
      'RBet',
      [stats.flopraise, stats.turnraise, stats.riverraise],
      [stats.flopraisepossible, stats.turnraisepossible, stats.riverraisepossible],
    ),
    col('bxb TOT', [null, null, stats.bxbtot], [null, null, stats.bxbtotpossible]),
    col('bxf TOT', [null, null, stats.bxftot], [null, null, stats.bxftotpossible]),
    col('bxb IP', [null, null, stats.bxbip], [null, null, stats.bxbippossible]),
    col('bxf IP', [null, null, stats.bxfip], [null, null, stats.bxfippossible]),
    col('bxb OOP', [null, null, stats.bxboop], [null, null, stats.bxbooppossible]),
    col('bxf OOP', [null, null, stats.bxfoop], [null, null, stats.bxfooppossible]),
    col(
      'B-F',
      [stats.betfoldflop, stats.betfoldturn, stats.betfoldriver],
      [stats.betfoldfloppossible, stats.betfoldturnpossible, stats.betfoldriverpossible],
    ),
    col('B-c&f t', [null, stats.bcallfoldturn, null], [null, stats.bcallfoldturnpossible, null]),
    col(
      'B-c&c t&f r',
      [null, null, stats.bcallcallfoldriver],
      [null, null, stats.bcallcallfoldriverpossible],
    ),
    col(
      'RF af c f&t',
      [null, null, stats.rfaftercallflopturnfold],
      [null, null, stats.rfaftercallflopturnfoldpossible],
    ),
    col(
      'RF af c tr',
      [null, null, stats.rfaftercallturnfold],
      [null, null, stats.rfaftercallturnfoldpossible],
    ),
  ];
}

export type PostflopViewKey = 'General' | 'Preflop' | 'Donk' | 'CBet' | 'B/x';

export type PreflopGroupId = 'open' | 'vs3bet';

export const PREFLOP_NAV: Array<{
  id: PreflopGroupId;
  label: string;
  tabs: PostflopSubTab[];
}> = [
  {
    id: 'open',
    label: 'Open / ISO',
    tabs: [
      { id: 'or-open', label: 'Total' },
      { id: 'pre-oop', label: 'OOP' },
      { id: 'pre-ip', label: 'IP' },
      { id: 'or-limp', label: 'Iso & Limp' },
    ],
  },
  {
    id: 'vs3bet',
    label: 'vs 3bet+',
    tabs: [
      { id: '3bet', label: '3bet' },
      { id: 'f4bet', label: 'F4bet' },
      { id: '5bet', label: '5bet' },
      { id: 'cc', label: 'CC' },
      { id: 'sq1c', label: 'Sq 1c' },
    ],
  },
];

export function getPreflopGroupForTab(tabId: string): PreflopGroupId {
  for (const group of PREFLOP_NAV) {
    if (group.tabs.some((tab) => tab.id === tabId)) {
      return group.id;
    }
  }
  return 'open';
}

export const POSTFLOP_SUB_TABS: Partial<Record<PostflopViewKey, PostflopSubTab[]>> = {
  General: [
    { id: 'g-bet', label: 'Info' },
    { id: 'g-cbet', label: 'CB' },
  ],
  Preflop: PREFLOP_NAV.flatMap((group) => group.tabs),
  Donk: [
    { id: 'd-donk', label: 'Donk' },
    { id: 'd-vs', label: 'vs Donk' },
  ],
  CBet: [
    { id: 'c-tot', label: 'CB' },
    { id: 'c-ip', label: 'CbetIP' },
    { id: 'c-oop', label: 'CbOOP' },
    { id: 'c-lines', label: 'Lines' },
    { id: 'c-vscb', label: 'vsCb' },
  ],
  'B/x': [
    { id: 'bx-tot', label: 'Total' },
    { id: 'bx-pos', label: 'Pos' },
    { id: 'bx-lines', label: 'Lines' },
  ],
};

const GENERAL_COLUMN_GROUPS: Record<string, string[]> = {
  'g-cbet': ['CBet', 'FvCb', 'RCb'],
  'g-bet': ['Bet', 'B-F', 'FvBet', 'RBet', 'W after R', 'Donk', 'xR', 'Agf %'],
};

const DONK_COLUMN_GROUPS: Record<string, string[]> = {
  'd-donk': ['Donk', 'D 3bp'],
  'd-vs': ['FvD', 'D-fold'],
};

const CBET_COLUMN_GROUPS: Record<string, string[]> = {
  'c-tot': ['CBet', 'Cb-fold', 'RCb'],
  'c-ip': ['CBet IP', 'Cb-f IP', 'bxb IP', 'bxf IP'],
  'c-oop': ['CBet OOP', 'Cb-f OOP', 'x_F', 'bxb OOP', 'bxf OOP'],
  'c-lines': ['Cb-c&f t', 'Cb-c&c t&f r'],
};

const VSCB_COLUMN_GROUPS: Record<string, string[]> = {
  'c-vscb': ['FvCb', 'RCb', 'FvCB IP', 'RCb IP', 'FvCB OOP', 'RCb OOP'],
};

const BX_COLUMN_GROUPS: Record<string, string[]> = {
  'bx-tot': ['Bet', 'FvBet', 'RBet', 'bxb TOT', 'bxf TOT', 'B-F'],
  'bx-pos': ['bxb IP', 'bxf IP', 'bxb OOP', 'bxf OOP'],
  'bx-lines': ['B-c&f t', 'B-c&c t&f r', 'RF af c f&t', 'RF af c tr'],
};

export function getPostflopViewTabs(view: PostflopViewKey): PostflopSubTab[] {
  return POSTFLOP_SUB_TABS[view] ?? [];
}

export function getDefaultSubTab(view: PostflopViewKey): string {
  return getPostflopViewTabs(view)[0]?.id ?? '';
}

export function buildPostflopPanel(
  stats: Partial<FullStatsDto>,
  view: PostflopViewKey,
  subTabId: string,
): PostflopPanelContent {
  switch (view) {
    case 'General':
      return buildStreetPanel(
        getPostflopViewTabs('General'),
        buildGeneralColumns(stats),
        GENERAL_COLUMN_GROUPS,
        subTabId,
      );
    case 'Donk':
      return buildStreetPanel(
        getPostflopViewTabs('Donk'),
        buildDonkColumns(stats),
        DONK_COLUMN_GROUPS,
        subTabId,
      );
    case 'CBet':
      return buildCbetPanel(stats, subTabId);
    case 'B/x':
      return buildStreetPanel(
        getPostflopViewTabs('B/x'),
        buildBxColumns(stats),
        BX_COLUMN_GROUPS,
        subTabId,
      );
    case 'Preflop':
      return buildPreflopPanel(stats, subTabId);
    default:
      return { tables: [{ table: null }] };
  }
}

function buildStreetPanel(
  subTabs: PostflopSubTab[],
  columns: ColumnDef[],
  groups: Record<string, string[]>,
  subTabId: string,
): PostflopPanelContent {
  const groupId = groups[subTabId] ? subTabId : (subTabs[0]?.id ?? '');
  const selectedColumns = pickColumns(columns, groups[groupId] ?? []);

  return {
    subTabs,
    tables: [{ table: selectedColumns.length ? columnsToTable(selectedColumns) : null }],
  };
}

function buildCbetPanel(stats: Partial<FullStatsDto>, subTabId: string): PostflopPanelContent {
  const subTabs = getPostflopViewTabs('CBet');
  const groupId = subTabs.some((tab) => tab.id === subTabId) ? subTabId : (subTabs[0]?.id ?? '');

  if (VSCB_COLUMN_GROUPS[groupId]) {
    return {
      subTabs,
      tables: [
        {
          table: columnsToTable(pickColumns(buildVsCbColumns(stats), VSCB_COLUMN_GROUPS[groupId])),
        },
      ],
    };
  }

  return buildStreetPanel(subTabs, buildCbetColumns(stats), CBET_COLUMN_GROUPS, groupId);
}

function buildPreflopPanel(stats: Partial<FullStatsDto>, subTabId: string): PostflopPanelContent {
  const subTabs = getPostflopViewTabs('Preflop');

  switch (subTabId) {
    case 'pre-ip':
      return {
        subTabs,
        tables: [{ table: buildPreflopOrTable(stats.preflopOr, PREFLOP_OR_IP_ROWS) }],
      };
    case 'pre-oop':
      return {
        subTabs,
        tables: [{ table: buildPreflopOrTable(stats.preflopOr, PREFLOP_OR_OOP_ROWS) }],
      };
    case '3bet':
      return {
        subTabs,
        tables: [{ table: buildThreeBetVsTable(stats.threeBetVs) }],
      };
    case 'f4bet':
      return {
        subTabs,
        tables: [{ table: buildThreeBetVsTable(stats.foldFourBetVs) }],
      };
    case '5bet':
      return {
        subTabs,
        tables: [{ table: buildThreeBetVsTable(stats.fiveBetVs) }],
      };
    case 'cc':
      return {
        subTabs,
        tables: [{ table: buildThreeBetVsTable(stats.coldCallVs, true) }],
      };
    case 'sq1c':
      return {
        subTabs,
        tables: [{ table: buildThreeBetVsTable(stats.squeeze1cVs) }],
      };
    case 'or-limp':
      return {
        subTabs,
        tables: [{ table: buildPreflopOrTable(stats.preflopOr, PREFLOP_OR_TOT_ROWS.slice(7)) }],
      };
    case 'or-open':
    case 'or':
    default:
      return {
        subTabs,
        tables: [{ table: buildPreflopOrTable(stats.preflopOr, PREFLOP_OR_TOT_ROWS.slice(0, 7)) }],
      };
  }
}

/** @deprecated Use buildPostflopPanel */
export function buildPostflopTable(
  stats: Partial<FullStatsDto>,
  view: PostflopViewKey,
): PostflopTableData | null {
  return buildPostflopPanel(stats, view, getDefaultSubTab(view)).tables[0]?.table ?? null;
}

export type PortableUltraStatEntry = {
  label: string;
  value: string | number;
};

function flattenPostflopTable(table: PostflopTableData): PortableUltraStatEntry[] {
  return table.rows.flatMap((row) =>
    table.heads.map((head, index) => {
      const value = row.cells[index]?.value;
      const label = table.heads.length === 1 ? row.label : `${head} ${row.label}`.trim();

      return {
        label,
        value: value ?? '—',
      };
    }),
  );
}

export function buildPortableUltraStats(stats: Partial<FullStatsDto>): PortableUltraStatEntry[] {
  const entries = new Map<string, string | number>();
  const views: PostflopViewKey[] = ['General', 'Preflop', 'Donk', 'CBet', 'B/x'];

  for (const view of views) {
    for (const tab of getPostflopViewTabs(view)) {
      const panel = buildPostflopPanel(stats, view, tab.id);

      for (const { table } of panel.tables) {
        if (!table) {
          continue;
        }

        for (const entry of flattenPostflopTable(table)) {
          if (!entries.has(entry.label)) {
            entries.set(entry.label, entry.value);
          }
        }
      }
    }
  }

  return Array.from(entries, ([label, value]) => ({ label, value }));
}
